/* eslint-disable @reatom/unit-naming-rule */
import {
  action,
  atom,
  batch,
  readonly,
  reatomAsync,
  sleep,
  withAbort,
  withAssign,
} from '@reatom/framework'

import { Result } from '@blade/std-lib'
import { ZeroSlot } from '@root/entities/network/model/solana'
import { ZeroSlotApiKey } from '@root/entities/trade-settings/model'
import { initializationStateAtom } from '@root/features/initialization/atoms/common'
import { InitializationState } from '@root/features/initialization/model'
import { heliusRpcLinksModel } from '@root/features/network/atoms'
import { dependenciesAtom } from '@root/features/shared/dependencies'

import { TradeSettingsRpcSnapshot } from '../model'

import { activePresetIndexAtom } from './reatom-trade-settings'

import type { TradeSettingsSolRpc } from '@root/entities/trade-settings/model'

export type RpcType = 'helius-sender' | '0slot' | 'custom'

const reatomTradeSettingsSolRpc = ({ preset: _ }: { preset: number }) => {
  const selectedHeliusRpcAtom = atom((ctx) => {
    const fastest = ctx.spy(heliusRpcLinksModel.fastest)
    return fastest
  })

  const selectedRpcTypeAtom = atom<RpcType>(
    'helius-sender',
    'selectedRpcTypeAtom',
  )

  const customRpcUrlAtom = atom('', 'customRpcUrlAtom')

  const zeroSlotApiKeyAtom = atom(ZeroSlotApiKey(''), 'zeroSlotApiKeyAtom')

  const zeroSlotRegionAtom = atom<ZeroSlot.RegionKey>(
    ZeroSlot.NeyYork.id,
    'zeroSlotRegionAtom',
  )

  const selectedRpcAtom = atom<TradeSettingsSolRpc.Rpc>((ctx) => {
    const rpcType = ctx.spy(selectedRpcTypeAtom)
    if (rpcType === 'custom') {
      const customRpcUrl = ctx.spy(customRpcUrlAtom)
      return {
        type: 'custom',
        url: customRpcUrl,
      }
    }

    if (rpcType === '0slot') {
      const zeroSlotRegion = ctx.spy(zeroSlotRegionAtom)
      const zeroSlotApiKey = ctx.spy(zeroSlotApiKeyAtom)
      const rpc: TradeSettingsSolRpc.Rpc = {
        type: '0slot',
        region: zeroSlotRegion,
        apiKey: zeroSlotApiKey,
      }
      return rpc
    }

    const heliusRpc = ctx.spy(selectedHeliusRpcAtom)
    return {
      type: 'helius-sender',
      location: heliusRpc,
    }
  })

  const setFromSnapshotAction = action(
    (ctx, snapshot: TradeSettingsRpcSnapshot.Snapshot | undefined) => {
      if (!snapshot) {
        return
      }
      selectedRpcTypeAtom(ctx, snapshot.type)
      customRpcUrlAtom(ctx, snapshot.customRpcUrl ?? '')
      zeroSlotApiKeyAtom(ctx, snapshot.apiKey ?? ZeroSlotApiKey(''))
      zeroSlotRegionAtom(ctx, snapshot.zeroSlotRegion ?? ZeroSlot.NeyYork.id)
    },
  )

  return selectedRpcAtom.pipe(
    withAssign(() => ({
      customRpcUrl: customRpcUrlAtom.pipe(readonly),
      setCustomRpcUrl: action((ctx, url: string) => {
        customRpcUrlAtom(ctx, url)
        persistSettingsAction(ctx)
      }),
      zeroSlotApiKey: zeroSlotApiKeyAtom.pipe(readonly),
      setZeroSlotApiKey: action((ctx, apiKey: ZeroSlotApiKey) => {
        zeroSlotApiKeyAtom(ctx, apiKey)
        persistSettingsAction(ctx)
      }),
      zeroSlotRegion: zeroSlotRegionAtom.pipe(readonly),
      setZeroSlotRegion: action((ctx, region: ZeroSlot.RegionKey) => {
        zeroSlotRegionAtom(ctx, region)
        persistSettingsAction(ctx)
      }),
      selectedRpcType: selectedRpcTypeAtom.pipe(readonly),
      setSelectedRpcType: action((ctx, type: RpcType) => {
        selectedRpcTypeAtom(ctx, type)
        persistSettingsAction(ctx)
      }),
      selectedRpc: selectedRpcAtom,
      setFromSnapshot: setFromSnapshotAction,
    })),
  )
}
export type SolRpcSettingsModel = ReturnType<typeof reatomTradeSettingsSolRpc>

export const solRpcSettingsModelByPreset = [
  reatomTradeSettingsSolRpc({ preset: 0 }),
  reatomTradeSettingsSolRpc({ preset: 1 }),
  reatomTradeSettingsSolRpc({ preset: 2 }),
  reatomTradeSettingsSolRpc({ preset: 3 }),
  reatomTradeSettingsSolRpc({ preset: 4 }),
] as const

export const solRpcSettingsModelAtom = atom((ctx) => {
  const activePresetIndex = ctx.spy(activePresetIndexAtom)
  return solRpcSettingsModelByPreset[activePresetIndex]
})

export const solSelectedRpcAtom = atom((ctx): TradeSettingsSolRpc.Rpc => {
  const solRpcSettingsModel = ctx.spy(solRpcSettingsModelAtom)
  if (!solRpcSettingsModel) {
    const fastest = ctx.spy(heliusRpcLinksModel.fastest)

    return {
      type: 'helius-sender',
      location: fastest,
    }
  }
  return ctx.spy(solRpcSettingsModel.selectedRpc)
})

const settingsLoadedAtom = atom(false, 'settingsLoadedAtom')
const loadPersistedSettingsAction = action(async (ctx) => {
  const optionsLoaded = ctx.get(settingsLoadedAtom)
  if (optionsLoaded) {
    return
  }
  const { secureStorageService } = ctx.get(dependenciesAtom)
  const persistedSettingsJson = await ctx.schedule(() =>
    secureStorageService.getApiSettings(),
  )
  batch(ctx, () => {
    settingsLoadedAtom(ctx, true)

    if (
      Result.isFailure(persistedSettingsJson) ||
      !persistedSettingsJson.success
    ) {
      return
    }

    const snapshots = TradeSettingsRpcSnapshot.fromJsonByPreset(
      persistedSettingsJson.success,
    )

    if (!snapshots) {
      return
    }

    for (const [preset, snapshot] of snapshots.entries()) {
      const model = solRpcSettingsModelByPreset[preset]
      if (!model) {
        continue
      }
      model.setFromSnapshot(ctx, snapshot)
    }
  })
})

const persistSettingsAction = reatomAsync(async (ctx) => {
  await ctx.schedule(() => sleep(500))

  const snapshots = solRpcSettingsModelByPreset.map(
    (model): TradeSettingsRpcSnapshot.Snapshot => ({
      type: ctx.get(model.selectedRpcType),
      customRpcUrl: ctx.get(model.customRpcUrl),
      apiKey: ctx.get(model.zeroSlotApiKey),
      zeroSlotRegion: ctx.get(model.zeroSlotRegion),
    }),
  )

  const json = TradeSettingsRpcSnapshot.toJsonByPreset(snapshots)
  if (!json) {
    return
  }
  const { secureStorageService } = ctx.get(dependenciesAtom)
  secureStorageService.setApiSettings(json)
}).pipe(withAbort())

export const initializeSolRpcSettingsAction = action((ctx) => {
  const unsubscribeInitialization = ctx.subscribe(
    initializationStateAtom,
    (initializationState) => {
      if (initializationState instanceof InitializationState.Ready) {
        loadPersistedSettingsAction(ctx)
      }
    },
  )

  return () => {
    unsubscribeInitialization()
  }
}, 'initializeAction')
